import {Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  Validator,
  Validators
} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {NotificationService} from '@services/notification/notification.service';
import {SortingService} from '@services/administration/sorting/sorting.service';
import {MachineService} from '@services/administration/machine/machine.service';
import {
  arrayPatternValidator
} from '@services/administration/machine/trigger/validators/array-pattern-validator.directive.service';

import {TriggerQueryAction} from '@models/adminstration/machine/trigger/trigger-query/trigger-query-action';
import {User} from '@models/user/user';
import {EMAIL_REGEX} from '@constants/reg-exp';
import {FirebaseAuthenticationService} from '@services/firebase/auth/firebase-authentication.service';
import {Role} from '@models/user/role';
import {ElasticSearchService} from '@services/elastic-search/elastic-search.service';
import {Subject, switchMap, takeUntil} from 'rxjs';


type ChangeCallbackFn<T> = (value: T) => void;
type TouchCallbackFn = () => void;

@Component({
    selector: 'scitis-admin-email-action-type',
    templateUrl: './email-action-type.component.html',
    styleUrls: ['./email-action-type.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => EmailActionTypeComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => EmailActionTypeComponent),
            multi: true
        }
    ],
    standalone: false
})
export class EmailActionTypeComponent implements ControlValueAccessor, Validator, OnInit, OnDestroy {

  @Input()
  userRole: Role = new Role(new User());

  @Input()
  triggerQueryAction: TriggerQueryAction = new TriggerQueryAction();

  @Input()
  addTriggerAction: boolean = false;

  @Output()
  updatedTriggerQueryAction: EventEmitter<TriggerQueryAction> = new EventEmitter<TriggerQueryAction>();

  @Output()
  formIsValid: EventEmitter<boolean> = new EventEmitter<boolean>();

  superOrTechAdminRole = false;
  isDistributorOrHigher = false;
  isLoadingSubscribers: boolean = false;
  userNames: string[] = new Array<string>();
  subscriberList: string[] = new Array<string>();
  triggerQueryEmailForm: UntypedFormGroup = this.fb.group({});
  userName = '';
  usernameSubscribed = false;
  private destroy$ = new Subject<boolean>();
  private CURRENT_PAGE_NAME: string = 'EmailActionTypeComponent';

  constructor(
      private fb: UntypedFormBuilder,
      private translate: TranslateService,
      private notificationService: NotificationService,
      private sortingService: SortingService,
      private machineService: MachineService,
      private firebaseAuthenticationService: FirebaseAuthenticationService,
      private elasticSearchService: ElasticSearchService) {
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.triggerQueryEmailForm.controls;
  }

  ngOnInit(): void {
    this.setSuperOrTechAdminRole();
    this.setDistributorRoleOrHigherFlag();
    this.setUserName();
    this.generateTriggerQueryEmailForm();
    this.setUserNames();

    if (!this.isDistributorOrHigher && this.addTriggerAction) {
      this.subscribeLoggedUser();
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  validate(): ValidationErrors | null {
    if (this.triggerQueryEmailForm.invalid) {
      return {invalidForm: {valid: false, message: 'EmailActionTypeComponent fields are invalid'}};
    }
    return null;
  }

  writeValue(val: any): void {
    if (val) {
      this.triggerQueryEmailForm.setValue(val, {emitEvent: false});
    }
  }

  registerOnChange(fn: ChangeCallbackFn<object>): void {
    this.triggerQueryEmailForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(fn);

  }

  registerOnTouched(fn: TouchCallbackFn): void {
    // needed to implement ControlValueAccessor interface
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.triggerQueryEmailForm.disable();
    } else {
      this.triggerQueryEmailForm.enable();
    }
  }

  onFocusOut(): void {
    this.onTriggerQueryEmailUpdated();
  }

  onNewSubscriberChange(subscriber: string): void {
    if (!subscriber.match(EMAIL_REGEX)) {
      this.userNames = this.userNames.filter(userName => userName !== subscriber);
      this.userNames = [...this.userNames].sort((a, b) => this.sortingService.sortStrings(a, b));
      this.triggerQueryEmailForm.controls.newSubscriber.setErrors({invalidEMail: true});
      return;
    }
    this.triggerQueryEmailForm.controls.newSubscriber.setValue('');
    if (!this.machineService.isNullOrEmptyOrWhiteSpace(subscriber) && this.subscriberList.indexOf(subscriber) < 0) {
      this.subscriberList.push(subscriber);
    }
    this.userNames = this.userNames.filter(userName => userName !== subscriber);
    this.userNames = [...this.userNames].sort((a, b) => this.sortingService.sortStrings(a, b));

    this.triggerQueryEmailForm.controls.subscriberList.setValue(this.subscriberList);
    this.triggerQueryEmailForm.controls.subscriberList.updateValueAndValidity();
    this.onFocusOut();
  }

  onRemoveSubscriber(subscriber: string): void {
    this.subscriberList = this.subscriberList.filter(item => item !== subscriber);
    if (!this.userNames.includes(subscriber)) {
      this.userNames.push(subscriber);
      this.userNames = [...this.userNames].sort((a, b) => this.sortingService.sortStrings(a, b));
    }
    this.triggerQueryEmailForm.controls.subscriberList.setValue(this.subscriberList);
    this.triggerQueryEmailForm.controls.subscriberList.updateValueAndValidity();
    if (!this.isDistributorOrHigher) {
      if (subscriber === this.userName) {
        this.usernameSubscribed = false;
      }
    }
    this.onFocusOut();
  }

  subscribeLoggedUser(): void {
    if (!this.subscriberList.includes(this.userName)) {
      this.userNames.push(this.userName);
      this.userNames = [...this.userNames].sort((a, b) => this.sortingService.sortStrings(a, b));
      if (this.subscriberList.indexOf(this.userName) === -1) {
        this.subscriberList.push(this.userName);
      }
      this.isLoadingSubscribers = false;
      this.triggerQueryEmailForm.controls.subscriberList.setValue(this.subscriberList);
      this.usernameSubscribed = true;
      this.onFocusOut();
    }
  }

  showRemoveSubscriptionButton(subscriber: string): boolean {
    let displayButton = true;
    if (!this.isDistributorOrHigher) {
      const emails = this.triggerQueryAction.email.split(';');
      emails.forEach((email: string) => {
        if (subscriber !== this.userName && email === subscriber) {
          displayButton = false;
        }
      });
    }
    return displayButton;
  }

  private setUserName(): void {
    this.firebaseAuthenticationService.getLoggedUserDetails().subscribe({
      next: (result: User) => this.userName = result.username,
      error: () => console.log('Unable to retrieve user details')
    });
  }

  private updateSubscriberList(): void {
    this.triggerQueryEmailForm.controls.subscriberList.setValue([...this.subscriberList]);
    this.onFocusOut();
  }

  private onTriggerQueryEmailUpdated() {
    const triggerQueryEmailUpdated = new TriggerQueryAction();

    triggerQueryEmailUpdated.newSubscriber = this.f.newSubscriber.value;

    triggerQueryEmailUpdated.subscriberList = [...this.f.subscriberList.value];
    this.f.subscriberList.updateValueAndValidity();

    triggerQueryEmailUpdated.subject = this.f.subject.value;
    triggerQueryEmailUpdated.message = this.f.message.value;

    this.updatedTriggerQueryAction.emit(triggerQueryEmailUpdated);
    this.formIsValid.emit(!this.triggerQueryEmailForm.invalid);
  }

  private setSuperOrTechAdminRole(): void {
    this.superOrTechAdminRole = this.userRole.isSuperAdminRole || this.userRole.isTechAdminRole;
  }

  private setDistributorRoleOrHigherFlag(): void {
    this.isDistributorOrHigher = this.userRole.isSuperAdminRole || this.userRole.isTechAdminRole || this.userRole.isAdminRole || this.userRole.isDistributorRole;
  }

  private setUserNames(): void {
    this.isLoadingSubscribers = true;
    this.firebaseAuthenticationService.getLoggedUserDetails().pipe(
        takeUntil(this.destroy$),
        switchMap(user => this.elasticSearchService.listAllAccessibleUsers(user))
    ).subscribe({
      next: (users => {
        let userNames = users.map(({username}) => username);
        userNames = userNames.sort((a, b) => this.sortingService.sortStrings(a, b));
        this.userNames = [...userNames];
        this.isLoadingSubscribers = false;
        this.setSubscriberList();
      }),
      error: (error => {
        console.error(error);
        this.isLoadingSubscribers = false;
        const message = this.translate.instant('Error getting users list.');
        this.notificationService.error(message, error, this.CURRENT_PAGE_NAME, true);
      })
    });
  }

  private generateTriggerQueryEmailForm(): void {

    this.triggerQueryEmailForm = this.fb.group({
      newSubscriber: [''],
      subscriberList: [[...this.triggerQueryAction.subscriberList], [Validators.required, arrayPatternValidator(EMAIL_REGEX)]],
      subject: [this.triggerQueryAction.subject, Validators.required],
      message: [this.triggerQueryAction.message, Validators.required]
    });

    this.formIsValid.emit(!this.triggerQueryEmailForm.invalid);
  }

  private setSubscriberList() {
    this.fillEmailAddressesNotUsersAndFillSubscriberList();
  }

  private fillEmailAddressesNotUsersAndFillSubscriberList() {
    if (this.triggerQueryAction.email) {
      const splitEmailAddresses = this.triggerQueryAction.email.split(';');
      splitEmailAddresses.forEach(tmp => {
        this.subscriberList.push(tmp);
        if (tmp === this.userName) {
          this.usernameSubscribed = true;
        }
        this.updateSubscriberList();
      });
    }
  }
}
